import React from 'react';
import cn from 'classnames';

import { IMessage, MessageStatus } from 'widget/types/messages';
import AvatarIcon from 'widget/shared/icons/AvatarIcon';
import { IconColor, IconSize } from 'widget/shared/icons/BaseIcon';
import { getContentStyleWithImageWidth } from 'widget/utils/getImageStyle';
import {
  isMediaAttachment,
  isDocumentAttachment
} from 'widget/utils/getAttachmentType';

import { getFormattedTime } from './utils';
import MessageBody from './MessageBody';
import MessageError from './MessageError';
import styles from './Message.module.scss';

export type MessageProps = {
  className?: string;
  message: IMessage;
  withAvatar: boolean;
  isFirst: boolean;
  botAvatarUrl?: string | null;
  onRetry?: (messageId: string) => void;
};

const Message: React.FC<MessageProps> = ({
  className,
  message,
  withAvatar,
  isFirst,
  botAvatarUrl,
  onRetry
}) => {
  const isPending = message.status === MessageStatus.PENDING;
  const isError = message.status === MessageStatus.ERROR;

  const mediaAttachments = React.useMemo(() => {
    if (!message.attachments) {
      return [];
    }

    return message.attachments.filter(isMediaAttachment);
  }, [message.attachments]);

  const documents = React.useMemo(() => {
    if (!message.attachments) {
      return [];
    }

    return message.attachments.filter(isDocumentAttachment);
  }, [message.attachments]);

  return (
    <div
      className={cn(
        styles['message'],
        message.isBot
          ? styles['message_from-bot']
          : styles['message_from-user'],
        isFirst && styles['message_first'],
        isPending && styles['message_pending'],
        isError && styles['message_error'],
        className
      )}
    >
      {withAvatar && (
        <div className={styles['message__avatar']}>
          {botAvatarUrl ? (
            <img
              className={styles['message__avatar-img']}
              src={botAvatarUrl}
              alt="AvatarIcon"
            />
          ) : (
            <AvatarIcon
              className={styles['message__avatar-icon']}
              size={IconSize.XL}
              color={IconColor.Brand}
            />
          )}
        </div>
      )}

      <div
        className={styles['message__content']}
        style={getContentStyleWithImageWidth(mediaAttachments)}
      >
        <MessageBody
          text={message.text}
          mediaAttachments={mediaAttachments}
          documents={documents}
          time={getFormattedTime(message.date)}
          isFromBot={message.isBot}
          isFirst={isFirst}
          isError={isError}
          textFormat={message.textFormat}
        />

        {isError && onRetry && (
          <MessageError messageId={message.id} onRetry={onRetry} />
        )}
      </div>
    </div>
  );
};

export default React.memo(Message);
